import React, { useCallback, useState } from 'react';

import { useApolloClient } from '@apollo/client/react';
import { Alert } from '@mui/material';
import { Helmet } from 'react-helmet';
import { ConfirmationDialog } from 'src/components';
import type { DocumentMetadataImportFieldsFragment } from 'src/gql/generated/apollo-types';
import * as Apollo from 'src/gql/generated/apollo-types';
import { useNotificationDispatch } from 'src/hooks';
import { NotificationsActionType } from 'src/notifications';
import { makeStyles } from 'tss-react/mui';

import { DataMigrationHistoryGrid } from './DataMigrationHistoryGrid';
import { useDataMigrationHistory } from './useDataMigrationHistory';
import { ImportDataWizard } from './wizard/ImportDataWizard';

const useStyles = makeStyles()(() => ({
  listContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0,
    position: 'relative',
  },
}));

export function DataMigrationPage(): JSX.Element {
  const { classes } = useStyles();
  const apolloClient = useApolloClient();
  const dispatch = useNotificationDispatch();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardOperationError, setWizardOperationError] = useState<string | null>(null);
  const [importToResume, setImportToResume] = useState<DocumentMetadataImportFieldsFragment | null>(
    null
  );
  const history = useDataMigrationHistory();

  const handleOpenWizard = useCallback(() => {
    if (history.hasRunningImport) return;

    setImportToResume(null);
    setWizardOperationError(null);
    setWizardOpen(true);
  }, [history.hasRunningImport]);

  const handleCloseWizard = useCallback(() => {
    setWizardOpen(false);
    setImportToResume(null);
    setWizardOperationError(null);
  }, []);

  const handleConfirmValidationError = useCallback(
    (options: { errorMessage: string; importRecord: DocumentMetadataImportFieldsFragment }) => {
      setImportToResume(options.importRecord);
      setWizardOperationError(options.errorMessage);
      setWizardOpen(true);
    },
    []
  );

  const handleOpenImport = useCallback(
    async (importId: string) => {
      try {
        const { data } = await apolloClient.query<
          Apollo.DocumentMetadataImportQuery,
          Apollo.DocumentMetadataImportQueryVariables
        >({
          fetchPolicy: 'network-only',
          query: Apollo.DocumentMetadataImportDocument,
          variables: { id: importId },
        });
        const importRecord = data?.documentMetadataImport;
        if (!importRecord) {
          dispatch({
            payload: {
              message: 'Could not load data migration preview.',
              variant: 'error',
            },
            type: NotificationsActionType.PUSH_NOTIFICATION,
          });
          return;
        }

        setImportToResume(importRecord);
        setWizardOpen(true);
      } catch (_error) {
        dispatch({
          payload: {
            message: 'Failed to load data migration preview.',
            variant: 'error',
          },
          type: NotificationsActionType.PUSH_NOTIFICATION,
        });
      }
    },
    [apolloClient, dispatch]
  );

  return (
    <>
      <Helmet>
        <title>Data Migration</title>
      </Helmet>

      <div className={classes.listContainer}>
        {history.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load import history.
          </Alert>
        )}
        <DataMigrationHistoryGrid
          {...history}
          onDelete={history.openDeleteConfirm}
          onNewImport={handleOpenWizard}
          onOpenImport={handleOpenImport}
          onSelectionModelChange={history.setSelectionModel}
        />
      </div>

      {wizardOpen && (
        <ImportDataWizard
          key={importToResume?.id ?? 'new-import'}
          existingImport={importToResume}
          initialOperationError={wizardOperationError}
          open={wizardOpen}
          onClose={handleCloseWizard}
          onConfirmValidationError={handleConfirmValidationError}
        />
      )}

      <ConfirmationDialog
        message={`Are you sure you want to delete the selected imports? This action cannot be undone.`}
        open={history.deleteConfirmOpen}
        primaryText="Delete"
        title="Delete imports?"
        onClickPrimary={history.handleDeleteConfirm}
        onClose={history.closeDeleteConfirm}
      />
    </>
  );
}
